/**
 * Export Tone to Library Dialog (v3)
 *
 * Right-edge SlideDrawer per the v3 design language: the library
 * tree and device memory stay visible while the drawer is open so
 * the operator keeps source-slot + destination context in view.
 *
 * Two body modes drawn into a single SlideDrawer (so the drawer
 * panel never remounts mid-operation):
 *   - Idle:  tone-name + tone-detail readout + Export action
 *   - Run/Done/Fail: shared step-log (`ac-step-*`) growing in
 *     place as `OperationProgress` ticks
 *
 * Replaces the legacy centered Radix.Dialog. The empty `catch {}`
 * silent-failure shape from BUG-001 disappears here: any thrown
 * error from `onExport` is captured into `localError`, coalesced
 * with `operationError`, and feeds the step log's failed-state row.
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import {
  SlideDrawer,
  StepRow,
  type ProgressStep,
} from '@audiocontrol/editor-core';
import { useDeviceConfig } from '@/context/DeviceConfigContext';
import type { S330Tone } from '@audiocontrol/sampler-devices/s330';
import { type OperationState } from '@/types/import-operation';
import { useExportDialogLifecycle } from '@/hooks/useExportDialogLifecycle';
import { DestinationEyebrow } from '@/components/library/DestinationEyebrow';

export interface ExportToneDialogProps extends OperationState {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tone: S330Tone | null;
  toneIndex: number;
  /** Library subfolder the export will land in (e.g. ['DRUMS']). When
   *  non-empty, the eyebrow row surfaces it so the operator sees the
   *  destination before clicking Export. Defaults to []. */
  targetPath?: string[];
  onExport: (toneName: string, toneIndex: number) => Promise<void>;
}

export function ExportToneDialog({
  open,
  onOpenChange,
  tone,
  toneIndex,
  targetPath,
  onExport,
  isOperating,
  progress,
  error: operationError,
}: ExportToneDialogProps): JSX.Element | null {
  const { memoryLayout, deviceName } = useDeviceConfig();
  const slotLabel = memoryLayout.formatToneSlot(toneIndex);

  const [toneName, setToneName] = useState(tone?.name || `Tone_${slotLabel}`);

  // Lifecycle hook owns localError, hasStarted, isComplete,
  // effectiveError, steps, handleClose. Per-dialog name state stays
  // here because the initial-name source differs across dialogs.
  const {
    localError,
    setLocalError,
    hasStarted,
    setHasStarted,
    isComplete,
    effectiveError,
    steps,
    handleClose,
  } = useExportDialogLifecycle({
    open,
    isOperating,
    progress,
    operationError,
    onOpenChange,
  });

  // Reset the per-dialog name when the drawer opens.
  useEffect(() => {
    if (open) {
      setToneName(tone?.name || `Tone_${slotLabel}`);
    }
  }, [open, tone?.name, slotLabel]);

  const handleExport = useCallback(async () => {
    const trimmed = toneName.trim();
    if (!trimmed) {
      setLocalError('Tone name is required');
      return;
    }
    setLocalError(null);
    setHasStarted(true);
    try {
      await onExport(trimmed, toneIndex);
    } catch (err) {
      // Coalesces with `operationError` via `effectiveError`. Without
      // this, the synchronous precondition throws in the parent hook
      // (e.g. `!libraryHandle`) would vanish — that is BUG-001.
      setLocalError(err instanceof Error ? err.message : 'Export failed');
    }
  }, [toneName, toneIndex, onExport, setLocalError, setHasStarted]);

  if (!open) return null;

  const footer = renderFooter({
    hasStarted,
    isComplete,
    hasError: !!effectiveError,
    isOperating,
    canExport: !!toneName.trim() && !!tone,
    onCancel: handleClose,
    onExport: handleExport,
    onClose: handleClose,
  });

  return (
    <SlideDrawer
      open={open}
      title="Export tone to library"
      onClose={handleClose}
      footer={footer}
    >
      {hasStarted ? (
        <StepLogBody
          steps={steps}
          isComplete={isComplete}
          hasError={!!effectiveError}
          successMessage={
            <>
              Tone exported successfully — saved as{' '}
              <span style={{ fontFamily: 'var(--ac-font-mono)' }}>
                {toneName.trim()}.yaml
              </span>
            </>
          }
        />
      ) : (
        <ToneFormBody
          tone={tone}
          slotLabel={slotLabel}
          deviceName={deviceName}
          toneName={toneName}
          setToneName={setToneName}
          targetPath={targetPath ?? []}
          error={localError}
        />
      )}
    </SlideDrawer>
  );
}

// ---------------------------------------------------------------------------
// Body — idle form
// ---------------------------------------------------------------------------

interface ToneFormBodyProps {
  tone: S330Tone | null;
  slotLabel: string;
  /** Active device name from useDeviceConfig (e.g. "S-330", "S-550") —
   *  routed in instead of hardcoded so the eyebrow correctly reflects
   *  whichever Roland surface the editor is mounted under. AUDIT-20260521-02. */
  deviceName: string;
  toneName: string;
  setToneName: (name: string) => void;
  targetPath: string[];
  error: string | null;
}

function ToneFormBody({
  tone,
  slotLabel,
  deviceName,
  toneName,
  setToneName,
  targetPath,
  error,
}: ToneFormBodyProps): JSX.Element {
  return (
    <div className="ac-export-form">
      <DestinationEyebrow
        kindLabel="TONE"
        leftField={slotLabel}
        deviceName={deviceName}
        targetPath={targetPath}
        testIdPrefix="export-tone"
      />
      <div className="ac-page-title-rule" aria-hidden="true" />

      <div className="ac-export-form-field">
        <label htmlFor="export-tone-name" className="ac-field-label">
          Tone name
        </label>
        <input
          id="export-tone-name"
          type="text"
          value={toneName}
          onChange={(e) => setToneName(e.target.value)}
          maxLength={32}
          autoFocus
          className={`ac-input ${error ? 'ac-input--error' : ''}`}
          placeholder="Enter tone name"
          data-testid="export-tone-name-input"
        />
        {error && (
          <p className="ac-export-form-error" role="alert">
            {error}
          </p>
        )}
      </div>

      {tone && (
        <dl className="ac-export-summary">
          <ExportSummaryRow label="Sample rate" value={tone.sampleRate} />
          <ExportSummaryRow
            label="Loop mode"
            value={tone.loopMode}
            valueStyle="capitalize"
          />
          <ExportSummaryRow label="Original key" value={String(tone.originalKey)} />
          <ExportSummaryRow label="TVF" value={tone.tvf.enabled ? 'ON' : 'OFF'} />
        </dl>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body — running / complete / failed step log
// ---------------------------------------------------------------------------

interface StepLogBodyProps {
  steps: ProgressStep[];
  isComplete: boolean;
  hasError: boolean;
  successMessage: ReactNode;
}

export function StepLogBody({
  steps,
  isComplete,
  hasError,
  successMessage,
}: StepLogBodyProps): JSX.Element {
  return (
    <div className="ac-stepped-progress">
      {steps.map((step) => (
        <StepRow key={step.id} step={step} />
      ))}
      {isComplete && !hasError && (
        <div className="ac-step-summary">{successMessage}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

interface ExportSummaryRowProps {
  label: string;
  value: string;
  valueStyle?: 'capitalize';
}

export function ExportSummaryRow({
  label,
  value,
  valueStyle,
}: ExportSummaryRowProps): JSX.Element {
  return (
    <div className="ac-export-summary-row">
      <dt>{label}</dt>
      <dd className={valueStyle === 'capitalize' ? 'ac-capitalize' : undefined}>
        {value}
      </dd>
    </div>
  );
}

interface FooterParams {
  hasStarted: boolean;
  isComplete: boolean;
  hasError: boolean;
  isOperating: boolean;
  canExport: boolean;
  onCancel: () => void;
  onExport: () => void;
  onClose: () => void;
  /** Verb shown on the primary action button + in the disabled
   *  in-flight tooltip. Defaults to "Export" so existing Export*
   *  call sites are unchanged. Import* call sites pass "Import". */
  verb?: string;
  /** Prefix for the two stable data-testids the footer emits:
   *  `${prefix}-cancel` on every Cancel/Close/Done button (one at a
   *  time), and `${prefix}-confirm` on the primary action button.
   *  Defaults to "export"; Import* call sites pass "import" so the
   *  testids become `import-cancel` / `import-confirm`. */
  testIdPrefix?: string;
}

export function renderFooter({
  hasStarted,
  isComplete,
  hasError,
  isOperating,
  canExport,
  onCancel,
  onExport,
  onClose,
  verb = 'Export',
  testIdPrefix = 'export',
}: FooterParams): JSX.Element {
  const cancelId = `${testIdPrefix}-cancel`;
  const confirmId = `${testIdPrefix}-confirm`;
  if (!hasStarted) {
    return (
      <>
        <button
          type="button"
          className="ac-btn ac-btn-sm"
          onClick={onCancel}
          data-testid={cancelId}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ac-btn ac-btn-sm ac-btn-primary"
          onClick={onExport}
          disabled={!canExport}
          data-testid={confirmId}
        >
          {verb}
        </button>
      </>
    );
  }
  if (isComplete && !hasError) {
    return (
      <button
        type="button"
        className="ac-btn ac-btn-sm ac-btn-primary"
        onClick={onClose}
        data-testid={cancelId}
      >
        Done
      </button>
    );
  }
  if (hasError) {
    return (
      <button
        type="button"
        className="ac-btn ac-btn-sm"
        onClick={onClose}
        data-testid={cancelId}
      >
        Close
      </button>
    );
  }
  // Running: cancel is unavailable today (the underlying op hook has
  // no abort signal). Show the button as disabled so the affordance
  // is visible and the test selector remains stable.
  return (
    <button
      type="button"
      className="ac-btn ac-btn-sm"
      disabled
      data-testid={cancelId}
      title={isOperating ? `${verb} in progress` : ''}
    >
      Cancel
    </button>
  );
}
